'use client'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'

import { CountDownBox } from '@/components/common/count-down-box'
import {
  getHomeHeroMobileDisplaySize,
  getHomeHeroMobileN7ImageUrl,
} from '@/components/common/home/hero-banner-image-params'
import { HeroBannerVideoSlide } from '@/components/common/home/hero-banner-video-slide'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { TBannerSlide } from '@/types/home'
import dayjs from 'dayjs'
import Autoplay from 'embla-carousel-autoplay'
import Image from 'next/image'
import { useMemo } from 'react'

type Props = {
  slides: TBannerSlide[]
  isMobile?: boolean
  homeType?: 'nam' | 'nu' | 'chung'
}

type TCountdownPosition =
  | 'top'
  | 'bottom'
  | 'center'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'

type TCountdownCustomPosition = {
  top?: string
  left?: string
  right?: string
  bottom?: string
}

const isCustomPosition = (
  position: TCountdownPosition | TCountdownCustomPosition | undefined
): position is TCountdownCustomPosition => {
  if (!position || typeof position === 'string') return false
  return (
    'top' in position ||
    'left' in position ||
    'right' in position ||
    'bottom' in position
  )
}

const getCountdownPositionClasses = (
  position?: {
    desktop?: TCountdownPosition | TCountdownCustomPosition
    mobile?: TCountdownPosition | TCountdownCustomPosition
  },
  isMobile?: boolean
): { className: string; style?: React.CSSProperties } => {
  const baseClasses = 'pointer-events-none absolute inset-0 z-10 flex'

  const positionMap: Record<TCountdownPosition, string> = {
    top: 'items-start justify-center pt-[50px] md:pt-[60px] lg:pt-[80px] 2xl:pt-[120px]',
    bottom:
      'items-end justify-center pb-[50px] md:pb-[60px] lg:pb-[80px] 2xl:pb-[120px]',
    center: 'items-center justify-center',
    'top-left':
      'items-start justify-start pl-4 pt-[50px] md:pl-[60px] md:pt-[60px] lg:pl-[80px] lg:pt-[80px] 2xl:pl-[120px] 2xl:pt-[120px]',
    'top-right':
      'items-start justify-end pr-4 pt-[50px] md:pr-[60px] md:pt-[60px] lg:pr-[80px] lg:pt-[80px] 2xl:pr-[120px] 2xl:pt-[120px]',
    'bottom-left':
      'items-end justify-start pl-4 pb-[50px] md:pl-[60px] md:pb-[60px] lg:pl-[80px] lg:pb-[80px] 2xl:pl-[120px] 2xl:pb-[120px]',
    'bottom-right':
      'items-end justify-end pr-4 pb-[50px] md:pr-[60px] md:pb-[60px] lg:pr-[80px] lg:pb-[80px] 2xl:pr-[120px] 2xl:pb-[120px]',
  }

  // Fallback logic:
  // - Nếu isMobile === true: dùng position.mobile → nếu không có thì dùng position.desktop → nếu không có thì dùng 'center'
  // - Nếu isMobile === false hoặc undefined: dùng position.desktop → nếu không có thì dùng 'center'
  const rawPosition =
    isMobile === true
      ? position?.mobile || position?.desktop || 'center'
      : position?.desktop || 'center'

  // Nếu là custom position (object có top/left/right/bottom)
  if (isCustomPosition(rawPosition)) {
    const customStyle: React.CSSProperties = {}

    // Chỉ set giá trị nếu không phải empty string và có giá trị
    if (rawPosition.top && rawPosition.top.trim() !== '') {
      customStyle.top = rawPosition.top
    }
    if (rawPosition.left && rawPosition.left.trim() !== '') {
      customStyle.left = rawPosition.left
    }
    if (rawPosition.right && rawPosition.right.trim() !== '') {
      customStyle.right = rawPosition.right
    }
    if (rawPosition.bottom && rawPosition.bottom.trim() !== '') {
      customStyle.bottom = rawPosition.bottom
    }

    // Khi dùng custom position, không dùng inset-0 để tránh conflict với custom styles
    // Chỉ dùng absolute, z-10, flex và pointer-events-none
    const customBaseClasses = 'pointer-events-none absolute z-10 flex'

    return {
      className: customBaseClasses,
      style: Object.keys(customStyle).length > 0 ? customStyle : undefined,
    }
  }

  // Nếu là preset position (string) hoặc fallback về 'center'
  const presetPosition: TCountdownPosition =
    typeof rawPosition === 'string' ? rawPosition : 'center'

  return {
    className: `${baseClasses} ${positionMap[presetPosition]}`,
  }
}

export const HeroBanner: React.FC<Props> = ({ slides, isMobile, homeType }) => {
  const mobileHeroDisplay = useMemo(() => getHomeHeroMobileDisplaySize(), [])

  const validSlides = useMemo(
    () =>
      slides.filter((slide) => {
        if (slide.hidden) return false

        if (slide.active_time) {
          const now = new Date().getTime()
          const start = slide.starts_at
            ? new Date(slide.starts_at).getTime()
            : 0
          const end = slide.ends_at
            ? new Date(slide.ends_at).getTime()
            : Infinity
          return now > start && now < end
        }

        return true
      }),
    [slides]
  )

  const itmSourceChange = (url: string, newValue: string): string => {
    try {
      const isRelative = url.startsWith('/')
      const base =
        typeof window !== 'undefined'
          ? window.location.origin
          : 'https://www.coolmate.me'
      const urlObj = new URL(url, base)

      if (urlObj.searchParams.has('itm_source')) {
        urlObj.searchParams.set('itm_source', newValue)
      } else {
        urlObj.searchParams.set('itm_source', 'homepage')
        // urlObj.searchParams.set('itm_medium', newValue)
      }

      if (isRelative) {
        const search = urlObj.searchParams.toString()
        return `${urlObj.pathname}${search ? `?${search}` : ''}${urlObj.hash}`
      }

      return urlObj.toString()
    } catch {
      return url
    }
  }

  const buildHeroBannerUrl = (slideUrl: string, itmSource: string): string => {
    const baseUrl = itmSourceChange(slideUrl, itmSource)
    const isRelative = baseUrl.startsWith('/')
    const origin = 'https://www.coolmate.me'
    const urlObj = new URL(baseUrl, origin)
    // NOTE(vn): Tạm thời không set itm_campaign và itm_content, chỉ giữ itm_source

    const searchString = urlObj.searchParams.toString()
    return isRelative
      ? `${urlObj.pathname}${searchString ? `?${searchString}` : ''}${urlObj.hash}`
      : urlObj.toString()
  }

  if (!validSlides.length) return null

  return (
    <section
      className='relative w-full max-sm:min-h-[500px] lg:min-h-[500px]'
      aria-label='Hero Banner Slideshow'
    >
      <h2 className='sr-only'>Featured Promotions and Collections</h2>
      <Carousel
        opts={{
          loop: true,
        }}
        plugins={[
          Autoplay({
            delay: 5000,
            stopOnInteraction: false,
            stopOnMouseEnter: true,
          }),
        ]}
      >
        <CarouselContent className='ml-0 h-full w-full'>
          {validSlides.map((slide, index) => {
            const altText = isMobile
              ? `Promotional banner ${index + 1} - mobile view`
              : `Promotional banner ${index + 1} - desktop view`
            const finalUrl = buildHeroBannerUrl(slide.url, altText)

            // Format tracking value based on homeType
            const trackingValue =
              homeType === 'nam'
                ? `home-nam__hero-banner__vitri-${index + 1}`
                : homeType === 'nu'
                  ? `home-nu__hero-banner__vitri-${index + 1}`
                  : `home__hero-banner__vitri-${index + 1}`

            return (
              <CarouselItem
                key={index}
                className='relative h-full w-full p-0'
                role='region'
                aria-label={`Promotional Banner ${index + 1} of ${validSlides.length}`}
              >
                {/* Video-hero slide: image-first progressive enhancement */}
                {slide.type === 'video' ? (
                  <HeroBannerVideoSlide
                    slide={slide}
                    index={index}
                    isMobile={!!isMobile}
                    trackingValue={trackingValue}
                    finalUrl={finalUrl}
                  />
                ) : (
                  <>
                    <a
                      href={finalUrl}
                      target={slide.link_target}
                      className='block h-full w-full'
                      ga-tracking-value={trackingValue}
                      ga-tracking-label={altText}
                      aria-label={`View promotion ${index + 1}`}
                    >
                      {isMobile ? (
                        <Image
                          src={getHomeHeroMobileN7ImageUrl(slide.mb_image)}
                          alt={altText}
                          width={mobileHeroDisplay.width}
                          height={mobileHeroDisplay.height}
                          className='w-full object-cover'
                          priority={index === 0}
                          loading={index === 0 ? 'eager' : 'lazy'}
                          fetchPriority={index === 0 ? 'high' : 'auto'}
                          sizes='(max-width: 768px) 100vw, 480px'
                          quality={80}
                        />
                      ) : (
                        <Image
                          // Width must match the ReactDOM.preload hint in page.tsx (1440w) —
                          // otherwise the browser fetches the full-resolution CDN original
                          // (observed 3840px wide) instead of the preloaded/resized asset.
                          src={ImageHelper.getImageUrl(slide.image, '', 2048)}
                          alt={altText}
                          width={2048}
                          height={787}
                          className='w-full object-cover'
                          priority={index === 0}
                          loading={index === 0 ? 'eager' : 'lazy'}
                          fetchPriority={index === 0 ? 'high' : 'auto'}
                          sizes='100vw'
                          // First slide is the LCP element. unoptimized bypasses /_next/image so
                          // the browser fetches the direct N7 CDN URL — which matches the
                          // ReactDOM.preload hint exactly, eliminating "Resource load delay".
                          unoptimized={index === 0}
                        />
                      )}
                    </a>

                    {/* Countdown from admin config */}
                    {slide.type === 'countdown' &&
                      slide.countdown_data?.end_at && (
                        <div
                          {...getCountdownPositionClasses(
                            slide.countdown_data.position,
                            isMobile
                          )}
                        >
                          <CountDownBox
                            expiryTimestamp={dayjs(
                              slide.countdown_data.end_at
                            ).toDate()}
                            size={slide.countdown_data.size || 'default'}
                          />
                        </div>
                      )}
                  </>
                )}
              </CarouselItem>
            )
          })}
        </CarouselContent>
        <CarouselNavigation />
      </Carousel>
    </section>
  )
}

function CarouselNavigation() {
  return (
    <>
      <CarouselPrevious
        className='absolute -left-4 top-1/2 h-10 w-10 -translate-y-1/2 border-none invert max-lg:hidden lg:left-1'
        variant='outline-neutral'
        hideIfStart
      />
      <CarouselNext
        className='absolute -right-4 top-1/2 h-10 w-10 -translate-y-1/2 border-none invert max-lg:hidden lg:right-1'
        variant='outline-neutral'
        hideIfEnd
      />
    </>
  )
}
